import React, {useState} from "react";
import {DatePicker, Tooltip} from "antd";
import {FileSearchOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {TableContainer, ActionButtons} from "./SubcompanyPayments.styles";
import {useFetch} from "../../../../hooks/useFetch";
import {useStore} from "../../../../hooks/useStore";
import {CustomButton} from "../../../_defaultComponents/CustomButton/CustomButton";
import moment, {locale} from "moment";

const TABLE_PREFIX = "paymentsTable";
const SubcompanyPayments = ({subcompanyId}) => {
    const {t} = useTranslation(["pools"]);
    const stores = useStore();
    const [currentPage, setCurrentPage] = useState(1);
    const defaultDateRange = [moment().subtract(30, "days"), moment()];
    const [selectedDateRange, setSelectedDateRange] = useState(defaultDateRange);

    const [loading, payments] = useFetch({
        fetchFn: stores.companyStore.listPayments.bind(null, {
            subCompanyId: subcompanyId,
            limit: 10,
            page: currentPage,
            startDate: selectedDateRange?.[0]
                ? moment(selectedDateRange[0]).format("YYYY-MM-DD")
                : moment()
                      .subtract(30, "days")
                      .format("YYYY-MM-DD"),
            endDate: selectedDateRange?.[1]
                ? moment(selectedDateRange[1]).format("YYYY-MM-DD")
                : moment().format("YYYY-MM-DD")
        }),
        dependencies: [currentPage, selectedDateRange],
        condition: Boolean(subcompanyId),
        initialCall: Boolean(subcompanyId)
    });

    const onClickDetailButton = url => {
        window.open(`${url}`, "_blank");
    };
    const renderActionButtons = ({payment, id}) => {
        const permisions = {
            show: true
        };
        return (
            <ActionButtons>
                {permisions.show && Boolean(payment?.dekontUrl) && (
                    <Tooltip title={"Dekont Görüntüle"}>
                        <CustomButton
                            data-testid="detailButton"
                            onClick={() => onClickDetailButton(payment?.dekontUrl)}
                            icon={<FileSearchOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    const resolveStatus = status => {
        switch (status) {
            case 1:
                return "Beklemede";
            case 2:
                return "Ödeme Alındı";
            case 3:
                return "Ödeme Alınamadı";
            default:
                return "-";
        }
    };

    return (
        <>
            <div>
                <h5>
                    Tarih Aralığı{" "}
                    <Tooltip title="Son 30 günün verisi gösterilmektedir.">
                        <InfoCircleOutlined />
                    </Tooltip>
                </h5>
                <DatePicker.RangePicker
                    placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                    value={selectedDateRange}
                    onChange={value => {
                        setSelectedDateRange(value);
                    }}
                    locale={locale}
                    style={{marginBottom: 20}}
                />
            </div>
            <TableContainer
                className={TABLE_PREFIX}
                pagination={{
                    total: payments?.total || 0,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                rowKey="_id"
                rowClassName="hello"
                dataSource={payments?.result || []}
                data-testid="poolDataTable"
                loading={loading}
            >
                <TableContainer.Column
                    title={t("actions.action", {ns: "common"})}
                    dataIndex="_id"
                    render={(id, payment) => {
                        return (
                            <span
                                data-testid="actionButtons"
                                data-label={t("actions.action", {ns: "common"})}
                            >
                                {renderActionButtons({payment, id})}
                            </span>
                        );
                    }}
                />
                <TableContainer.Column
                    dataIndex="createdAt"
                    title={"İşlem Tarihi"}
                    render={(_, row) => (
                        <span data-testid="createdAt" data-label={`İşlem Tarihi`}>
                            {(row?.createdAt && moment(row?.createdAt).format("DD-MM-YYYY")) || "-"}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="refNo"
                    title={"Ref No"}
                    ellipsis={{showTitle: false}}
                    render={refNo => (
                        <span data-testid="refNo" data-label={`Ref No:`}>
                            <Tooltip placement="topLeft" title={refNo}>
                                {refNo}
                            </Tooltip>
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="status"
                    title={t("fields.status", {ns: "common"})}
                    render={status => (
                        <span
                            data-testid="status"
                            data-label={`${t("fields.status", {ns: "common"})}:`}
                        >
                            {resolveStatus(status)}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="subCompanyName"
                    title={t("fields.subcompany", {ns: "common"})}
                    render={subCompanyName => (
                        <span
                            data-testid="subCompanyName"
                            data-label={`${t("fields.subcompany", {ns: "common"})}:`}
                        >
                            {subCompanyName || "-"}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="amount"
                    title={"Tutar"}
                    render={amount => (
                        <span data-testid="amount" data-label={`Tutar:`}>
                            {amount || "-"}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="vatAmount"
                    title={"KDV Tutarı"}
                    render={vatAmount => (
                        <span data-testid="vatAmount" data-label={`KDV Tutar:`}>
                            {vatAmount || "-"}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="amountWithVat"
                    title={"KDV Dahil Tutar"}
                    render={(_, row) => (
                        <span data-testid="amountWithVat" data-label={`KDV Dahil Tutar:`}>
                            {row?.amount + row?.vatAmount || "-"}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="creditCardNumber"
                    title={"Kart Numarası"}
                    ellipsis={{showTitle: false}}
                    render={creditCardNumber => (
                        <span data-testid="creditCardNumber" data-label={`Kart Numarası`}>
                            <Tooltip placement="topLeft" title={creditCardNumber}>
                                {creditCardNumber || "-"}
                            </Tooltip>
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="message"
                    title={"Mesaj"}
                    ellipsis={{showTitle: false}}
                    render={message => (
                        <span data-testid="message" data-label={`Mesaj:`}>
                            <Tooltip placement="topLeft" title={message}>
                                {message || "-"}
                            </Tooltip>
                        </span>
                    )}
                />
            </TableContainer>
        </>
    );
};

export default SubcompanyPayments;
